'use client';
import React from 'react';
import { Act, getActStatus, getStageColor, Stage } from '@/data/scheduleData';
import { Crown, Heart } from 'lucide-react';
import { useFavorites } from '@/contexts/FavoritesContext';

interface ActListProps {
  acts: Act[];
  dayDate: string;
  now: Date;
  emptyMessage: string;
  eventId: string;
}

const stageDotColor: Record<Stage, string> = {
  'Musikarena': 'bg-primary',
  'Marktbühne': 'bg-green-700',
  'Akrobatik-Platz': 'bg-purple-600',
  'Taverne': 'bg-amber-600',
};

export default function ActList({ acts, dayDate, now, emptyMessage, eventId }: ActListProps) {
  const { isFavorite, toggleFavorite } = useFavorites();

  if (acts.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-4xl mb-3">🎭</p>
        <p className="font-display text-lg font-semibold text-primary">Keine Acts</p>
        <p className="text-sm text-muted-foreground mt-1">{emptyMessage}</p>
      </div>
    );
  }

  const sorted = [...acts].sort((a, b) => a.startTime.localeCompare(b.startTime));

  return (
    <div className="space-y-2">
      {sorted.map((act) => {
        const status = getActStatus(act, dayDate, now);
        const fav = isFavorite(act.id, eventId);

        return (
          <div
            key={`act-${act.id}`}
            className={`rounded-xl border border-border overflow-hidden transition-all duration-200 hover:shadow-card-hover ${
              status === 'live' ? 'act-row-live border-primary/30' :
              status === 'finished' ? 'act-row-finished' : 'act-row-upcoming bg-card'
            }`}
          >
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Time column */}
              <div className="flex-shrink-0 w-16 text-right">
                <span className="font-mono text-sm font-semibold text-foreground tabular-nums">
                  {act.startTime}
                </span>
                <span className="block font-mono text-xs text-muted-foreground tabular-nums">
                  {act.endTime}
                </span>
              </div>

              {/* Stage dot */}
              <div className="flex-shrink-0">
                <span className={`w-2.5 h-2.5 rounded-full block ${stageDotColor[act.stage] || 'bg-muted-foreground'}`} />
              </div>

              {/* Act info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold text-sm ${status === 'finished' ? 'line-through' : 'text-foreground'}`}>
                    {act.name}
                  </span>
                  {act.isHeadliner && (
                    <Crown size={12} className="text-accent flex-shrink-0" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStageColor(act.stage)}`}>
                    {act.stage}
                  </span>
                  <span className="text-xs text-muted-foreground">{act.genre}</span>
                </div>
              </div>

              {/* Favorite + Status */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => toggleFavorite(act.id, 'act', eventId, act.name)}
                  className={`p-1.5 rounded-full transition-colors ${
                    fav
                      ? 'text-red-500 bg-red-50 hover:bg-red-100' :'text-muted-foreground hover:text-red-400 hover:bg-red-50'
                  }`}
                  aria-label={fav ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                >
                  <Heart size={14} fill={fav ? 'currentColor' : 'none'} />
                </button>

                {status === 'live' && (
                  <span className="badge-live text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    Live
                  </span>
                )}
                {status === 'upcoming' && (() => {
                  const [day, month, year] = dayDate.split('.').map(Number);
                  const [startH, startM] = act.startTime.split(':').map(Number);
                  const actStart = new Date(year, month - 1, day, startH, startM);
                  const diffMs = actStart.getTime() - now.getTime();
                  const twoHoursMs = 2 * 60 * 60 * 1000;
                  return diffMs <= twoHoursMs ? (
                    <span className="badge-upcoming text-xs px-2.5 py-1 rounded-full font-medium">
                      Bald
                    </span>
                  ) : null;
                })()}
                {status === 'finished' && (
                  <span className="badge-finished text-xs px-2.5 py-1 rounded-full font-medium">
                    Vorbei
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}